import type { VendorInput } from '../../shared/contract/quotations.js';
import { VENDOR_TYPES, type VendorType } from '../../shared/enums.js';
import { elValue, selectValue } from '../lib/dom.js';
import { supplierLeads } from '../lib/supplier-leads.js';
import { showToast } from '../lib/toast.js';
import { api } from './api.js';
import { loadVendors } from './loaders.js';
import { analysisReport, me, vendorDetail, vendorDialog } from './state.js';

/** Open the Vendors dialog to add a vendor (`null`) or edit one (HS2-BBZXKM). */
export function openVendorDialog(vendorId: string | null): void {
  vendorDialog.value = { vendorId, error: '' };
}

export function closeVendorDialog(): void {
  vendorDialog.value = null;
}

/** Save a seller identified on a fetched benchmark page as an organization vendor. */
export async function addSupplierLeadAsVendor(benchmarkId: string): Promise<void> {
  const orgId = me.value?.organizationId;
  const report = analysisReport.value;
  if (orgId === null || orgId === undefined || report === null) return;
  if (me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') !== true) return;
  const lead = supplierLeads(report.benchmarks, report.verdicts).find((item) => item.benchmarkId === benchmarkId);
  if (lead === undefined) return;
  const result = await api.createVendor(orgId, {
    name: lead.name,
    type: lead.origin === 'import' ? 'foreign' : null,
    city: lead.location.includes(',') ? lead.location.split(',')[0]?.trim().slice(0, 120) : null,
    country: lead.country.split(',').at(-1)?.trim().slice(0, 120),
    notes: `Sourcing lead from pricing check: ${lead.url}`.slice(0, 4000),
  });
  if (!result.ok) {
    showToast(
      result.status === 409 ? `Vendor “${lead.name}” already exists.` : (result.error ?? 'Could not add vendor.'),
      'danger',
    );
    return;
  }
  showToast(`Vendor “${lead.name}” added.`);
  await loadVendors();
}

/** Save the open Vendors dialog; a failure stays in the dialog with its message. */
export async function saveVendor(root: HTMLElement): Promise<void> {
  const open = vendorDialog.value;
  const orgId = me.value?.organizationId;
  const dialog = root.querySelector('[data-action="vendor-dialog"]');
  if (open === null || orgId === null || orgId === undefined || dialog === null) return;
  const type = selectValue(dialog.querySelector('[name="vendor-type"]'));
  const body: VendorInput = {
    name: elValue(dialog.querySelector('[name="vendor-name"]')).trim(),
    type: VENDOR_TYPES.includes(type as VendorType) ? (type as VendorType) : null,
    city: elValue(dialog.querySelector('[name="vendor-city"]')).trim(),
    country: elValue(dialog.querySelector('[name="vendor-country"]')).trim(),
    notes: elValue(dialog.querySelector('[name="vendor-notes"]')).trim(),
  };
  if (body.name === '') {
    vendorDialog.value = { ...open, error: 'Enter a vendor name.' };
    return;
  }
  const res =
    open.vendorId === null ? await api.createVendor(orgId, body) : await api.updateVendor(orgId, open.vendorId, body);
  if (!res.ok) {
    vendorDialog.value = { ...open, error: res.error ?? 'Could not save the vendor.' };
    return;
  }
  vendorDialog.value = null;
  showToast(open.vendorId === null ? `Vendor “${body.name}” added.` : 'Vendor saved.');
  // Refresh the open detail in place (keeping its section) and the overview list.
  if (open.vendorId !== null && vendorDetail.value?.vendor.id === open.vendorId) {
    const detail = await api.vendorHistory(orgId, open.vendorId);
    if (detail.ok && detail.data !== null) vendorDetail.value = detail.data;
  }
  await loadVendors();
}
